/** @jsx jsx */
import React, { type ReactElement, useState, useCallback, type KeyboardEvent } from 'react';
import { styled, jsx } from '@compiled/react';
import keycode from 'keycode';
import Badge, { type BadgeProps } from '@atlaskit/badge';
import { Box } from '@atlaskit/primitives';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

const styles = cssMap({
	estimationBadgeWrapperOld: {
		marginLeft: token('space.050'),
		fontWeight: token('font.weight.bold'),
		display: 'inline-flex',
	},
	estimationBadgeWrapper: {
		marginLeft: token('space.050'),
		fontWeight: token('font.weight.bold'),
		display: 'inline-flex',
		cursor: 'default',
	},
});

export type Props = {
	appearance?: BadgeProps['appearance'];
	style?: BadgeProps['style'];
	children: number | string;
	max: number;
	screenReaderMessage: string;
	tooltipContent: ReactElement | string;
	tooltipDelay?: number; // Required for visual regression testing of the tooltip
};

const EstimationsBadge = ({
	appearance,
	style,
	children,
	max,
	screenReaderMessage,
	tooltipContent,
	tooltipDelay,
}: Props) => {
	const [active, setActive] = useState<boolean>(false);
	const onFocus = useCallback(() => {
		setActive(true);
	}, []);
	const onBlur = useCallback(() => {
		setActive(false);
	}, []);
	const onKeyDown = useCallback((event: KeyboardEvent<HTMLSpanElement>) => {
		if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			setActive((prev) => !prev);
		}
	}, []);

	const tooltipMessage = active ? tooltipContent : '';

	const content = isVisualRefreshEnabled() ? (
		<div aria-hidden>
			<Badge appearance={appearance} max={max}>
				{children}
			</Badge>
		</div>
	) : (
		<div aria-hidden>
			<Badge
				appearance={appearance}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={style}
				max={max}
			>
				{children}
			</Badge>
		</div>
	);

	const screenReaderId = screenReaderMessage.replaceAll(' ', '-');
	const visualyRefreshedBadgeWrapper = () => (
		<>
			<Box
				onFocus={onFocus}
				onBlur={onBlur}
				onMouseLeave={onBlur}
				onMouseEnter={onFocus}
				onKeyDown={onKeyDown}
				role="status"
				aria-live="polite"
				testId="software-backlog.card-list.estimations.badge.estimation-badge-wrapper"
				xcss={styles.estimationBadgeWrapper}
				aria-labelledby={screenReaderId}
			>
				{content}
			</Box>
			<VisuallyHidden id={screenReaderId}>{screenReaderMessage}</VisuallyHidden>
		</>
	);

	const nonVisualyRefreshedBadgeWrapper = () => (
		<>
			<EstimationBadgeWrapper
				onFocus={onFocus}
				onBlur={onBlur}
				onMouseLeave={onBlur}
				onMouseEnter={onFocus}
				onKeyDown={onKeyDown}
				css={legacyEstimationBadgeWrapperStyle.default}
				role="status"
				aria-live="polite"
				aria-labelledby={screenReaderId}
				data-testid="software-backlog.card-list.estimations.badge.estimation-badge-wrapper"
			>
				{content}
			</EstimationBadgeWrapper>
			<VisuallyHidden id={screenReaderId}>{screenReaderMessage}</VisuallyHidden>
		</>
	);

	return (
		<Tooltip content={tooltipMessage} delay={tooltipDelay}>
			{isVisualRefreshEnabled() && fg('visual-refresh_drop_3')
				? visualyRefreshedBadgeWrapper()
				: nonVisualyRefreshedBadgeWrapper()}
		</Tooltip>
	);
};

export default EstimationsBadge;

const legacyEstimationBadgeWrapperStyle = cssMap({
	default: {
		display: 'inline-flex',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EstimationBadgeWrapper = styled.span({
	marginLeft: token('space.050'),
	fontWeight: token('font.weight.bold'),
});
